import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { resolveImageUrl } from "@/utils/imageUrl";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import ProductCard from "@/components/ProductCard";
import { AnimatedCounter } from "@/components/AnimatedCounter";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import RecommendationsCarousel from "@/components/RecommendationsCarousel";
import { useSiteCustomization } from "@/context/SiteCustomizationContext";
import {
  Download,
  CheckCircle,
  Smartphone,
  Star,
  ArrowRight,
  GraduationCap,
  Search,

  Shield,
  Users,
  Play,
  ChevronRight,
  X
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_IMAGES = {
  hero: "https://customer-assets.emergentagent.com/job_doc-deploy-2/artifacts/n4jmpo8p_WhatsApp%20Image%202026-02-09%20at%209.31.47%20PM.jpeg",
  classroom: "https://customer-assets.emergentagent.com/job_doc-deploy-2/artifacts/22gq6ict_WhatsApp%20Image%202026-02-09%20at%209.41.16%20PM.jpeg",
  students: "https://customer-assets.emergentagent.com/job_doc-deploy-2/artifacts/qgz5ug4l_WhatsApp%20Image%202026-02-09%20at%2011.03.36%20PM.jpeg",
  examHall: "https://customer-assets.emergentagent.com/job_doc-deploy-2/artifacts/t2tw76x1_WhatsApp%20Image%202026-02-09%20at%2011.10.54%20PM.jpeg",
  happyStudents: "https://customer-assets.emergentagent.com/job_bf28faf8-971e-4f4e-94e5-2e197ca6b6dd/artifacts/9o1fdu8q_WhatsApp%20Image%202026-02-10%20at%204.33.30%20PM.jpeg",
};

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.15, ease: [0.25, 0.46, 0.45, 0.94] },
  }),
};

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.85 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] },
  },
};

function AnimatedSection({ children, className = "", delay = 0 }) {
  const [ref, isVisible] = useScrollAnimation(0.1);
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${className} ${
        isVisible
          ? "opacity-100 translate-y-0"
          : "opacity-0 translate-y-10"
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

export default function HomePage() {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [banners, setBanners] = useState([]);
  const [dismissedBanners, setDismissedBanners] = useState([]);
  const { isSectionEnabled, getContent, config: siteConfig } = useSiteCustomization();

  // Resolve image: use custom image from admin if set, else fallback to default
  const getImage = (key) => {
    const custom = siteConfig?.images?.[key];
    return resolveImageUrl(custom) || DEFAULT_IMAGES[key] || "";
  };

  useEffect(() => {
    fetchFeaturedProducts();
    fetchBanners();
  }, []);

  const fetchFeaturedProducts = async () => {
    try {
      const response = await axios.get(`${API}/products/featured?limit=6`);
      setFeaturedProducts(response.data);
    } catch (error) {
      console.error("Error fetching featured products:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBanners = async () => {
    try {
      const response = await axios.get(`${API}/banners/active`);
      setBanners(response.data);
    } catch (error) {
      console.error("Error fetching banners:", error);
    }
  };

  const dismissBanner = (id) => {
    setDismissedBanners(prev => [...prev, id]);
  };

  const activeBanners = banners.filter(b => !dismissedBanners.includes(b.id));

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      window.location.href = `/mock-exams?search=${encodeURIComponent(searchQuery)}`;
    }
  };

  const stats = [
    { value: getContent("stat_1_value", "500+"), label: getContent("stat_1_label", "Downloads") },
    { value: getContent("stat_2_value", "50+"), label: getContent("stat_2_label", "Partner Schools") },
    { value: getContent("stat_3_value", "95%"), label: getContent("stat_3_label", "Pass Rate") },
    { value: getContent("stat_4_value", "24/7"), label: getContent("stat_4_label", "Access"), isText: true },
  ];

  const features = [
    {
      icon: Download,
      title: getContent("feature_1_title", "Instant Delivery"),
      description: getContent("feature_1_desc", "Get your mock exams within 60 seconds. PDF downloads delivered straight to your email."),
    },
    {
      icon: GraduationCap,
      title: getContent("feature_2_title", "GES Aligned"),
      description: getContent("feature_2_desc", "All content aligned with Ghana Education Service syllabus (2024 to date)."),
    },
    {
      icon: Smartphone,
      title: getContent("feature_3_title", "Mobile Money"),
      description: getContent("feature_3_desc", "Pay easily with MTN MoMo, Vodafone Cash, or AirtelTigo Money."),
    },
    {
      icon: Shield,
      title: getContent("feature_4_title", "Secure Downloads"),
      description: getContent("feature_4_desc", "Watermarked PDFs for your protection with buyer-specific security."),
    },
  ];

  const testimonials = [
    {
      name: getContent("testimonial_1_name", "Mrs. Abena Mensah"),
      role: getContent("testimonial_1_role", "JHS Teacher, Accra"),
      content: getContent("testimonial_1_content", "The mock exams are perfectly aligned with the GES syllabus. My students' performance has improved significantly since we started using them."),
    },
    {
      name: getContent("testimonial_2_name", "Kwame Asante"),
      role: getContent("testimonial_2_role", "Basic 9 Student"),
      content: getContent("testimonial_2_content", "I passed my BECE with flying colors! The practice questions were very similar to the actual exam. Highly recommended."),
    },
    {
      name: getContent("testimonial_3_name", "Dr. Isaac Owusu"),
      role: getContent("testimonial_3_role", "School Administrator"),
      content: getContent("testimonial_3_content", "Bulk ordering for our school was seamless. Great quality materials at affordable prices. Our pass rate went up 20%."),
    },
  ];

  return (
    <div className="min-h-screen overflow-hidden">
      {/* ═══ PROMOTIONAL BANNERS ═══ */}
      {isSectionEnabled("promo_banners") && activeBanners.length > 0 && (
        <div className="fixed top-20 left-0 right-0 z-40" data-testid="promo-banner-container">
          {activeBanners.slice(0, 1).map(banner => (
            <motion.div
              key={banner.id}
              initial={{ y: -50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -50, opacity: 0 }}
              className="relative"
              style={{ backgroundColor: banner.bg_color, color: banner.text_color }}
              data-testid={`promo-banner-${banner.id}`}
            >
              <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-center gap-4 text-sm">
                <div className="flex items-center gap-3 flex-wrap justify-center">
                  <span className="font-bold">{banner.title}</span>
                  {banner.subtitle && <span className="opacity-80 hidden sm:inline">— {banner.subtitle}</span>}
                  {banner.cta_text && banner.cta_link && (
                    <Link
                      to={banner.cta_link}
                      className="inline-flex items-center gap-1 bg-white/20 hover:bg-white/30 backdrop-blur-sm px-4 py-1 rounded-full font-medium transition-colors text-xs"
                    >
                      {banner.cta_text}
                      <ArrowRight className="w-3 h-3" />
                    </Link>
                  )}
                </div>
                <button
                  onClick={() => dismissBanner(banner.id)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 opacity-60 hover:opacity-100 transition-opacity"
                  data-testid="dismiss-banner-btn"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* ═══ HERO SECTION ═══ */}
      {isSectionEnabled("hero") && (
      <section className="relative min-h-[95vh] flex items-center" data-testid="hero-section">
        {/* Background Image with Ken Burns effect */}
        <div className="absolute inset-0 overflow-hidden">
          <motion.div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat scale-105"
            style={{ backgroundImage: `url('${getImage("hero")}')` }}
            animate={{ scale: [1.05, 1.12, 1.05] }}
            transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
          />
        </div>
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-navy-900/95 via-navy-900/80 to-navy-900/40" />
        {/* Decorative floating shapes */}
        <motion.div
          className="absolute top-20 right-20 w-72 h-72 rounded-full bg-accent/10 blur-3xl"
          animate={{ x: [0, 30, 0], y: [0, -20, 0] }}
          transition={{ duration: 8, repeat: Infinity }}
        />
        <motion.div
          className="absolute bottom-40 left-10 w-56 h-56 rounded-full bg-white/5 blur-2xl"
          animate={{ x: [0, -20, 0], y: [0, 30, 0] }}
          transition={{ duration: 10, repeat: Infinity }}
        />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 w-full">
          <div className="max-w-2xl">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 mb-8"
            >
              <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
              <span className="text-sm text-white/90 font-medium">{getContent("hero_badge", "Trusted by 50+ Schools in Ghana")}</span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6"
              data-testid="hero-heading"
            >
              {getContent("hero_title", "Achieve Your Best")}{" "}
              <span className="relative inline-block">
                <span className="relative z-10 text-red-500">{getContent("hero_highlight", "BECE Results")}</span>
                <motion.span
                  className="absolute bottom-1 left-0 right-0 h-3 bg-accent/40 -z-0"
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ duration: 0.6, delay: 1 }}
                  style={{ originX: 0 }}
                />
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-lg text-gray-200 mb-10 leading-relaxed max-w-xl"
            >
              {getContent("hero_subtitle", "Quality mock examinations designed by experienced educators. Instant digital delivery with comprehensive marking schemes.")}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="flex flex-wrap gap-4"
            >
              <Button
                asChild
                className="btn-pill bg-accent hover:bg-accent/90 text-white text-base h-14 shadow-lg shadow-accent/25 group"
                data-testid="browse-exams-btn"
              >
                <Link to="/mock-exams">
                  {getContent("hero_cta_primary", "Browse Mock Exams")}
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                className="btn-pill border-2 border-white/50 text-white hover:bg-white/10 hover:border-white text-base h-14 backdrop-blur-sm"
              >
                <Link to="/school-services">
                  {getContent("hero_cta_secondary", "School Bulk Orders")}
                </Link>
              </Button>
            </motion.div>

            {/* Quick trust indicators */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 1 }}
              className="flex flex-wrap items-center gap-6 mt-12 text-sm text-white/70"
            >
              {["GES Aligned", "Instant Download", "Mobile Money"].map((item, i) => (
                <span key={i} className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  {item}
                </span>
              ))}
            </motion.div>
          </div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-1.5">
            <motion.div
              className="w-1.5 h-3 bg-white/60 rounded-full"
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
          </div>
        </motion.div>
      </section>
      )}

      {/* ═══ SEARCH SECTION ═══ */}
      {isSectionEnabled("search_bar") && (
      <section className="bg-white py-0 -mt-12 relative z-20">
        <AnimatedSection className="max-w-4xl mx-auto px-4">
          <div className="bg-white rounded-2xl shadow-2xl shadow-navy-900/10 p-6 border border-gray-100">
            <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-4" data-testid="search-form">
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search by subject or class level..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 h-14 text-base border-gray-200 rounded-xl focus:ring-accent"
                  data-testid="hero-search"
                />
              </div>
              <Button
                type="submit"
                className="h-14 px-8 bg-accent hover:bg-accent/90 text-white rounded-xl text-base font-semibold shadow-lg shadow-accent/20"
                data-testid="search-submit"
              >
                Search Exams
              </Button>
            </form>
          </div>
        </AnimatedSection>
      </section>
      )}

      {/* ═══ STATS SECTION ═══ */}
      {isSectionEnabled("stats") && (
      <section className="relative py-20 overflow-hidden" data-testid="stats-section">
        <div className="absolute inset-0 bg-navy-900" />
        <div className="absolute inset-0 opacity-10 bg-cover bg-center" style={{ backgroundImage: `url('${getImage("examHall")}')` }} />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <AnimatedSection key={index} delay={index * 100}>
                <div className="text-center group" data-testid={`stat-${index}`}>
                  <p className="text-4xl md:text-5xl font-bold text-white mb-2 tabular-nums">
                    {stat.value}
                  </p>
                  <p className="text-gray-400 text-sm uppercase tracking-wider">{stat.label}</p>
                  <div className="w-8 h-0.5 bg-accent mx-auto mt-3 group-hover:w-16 transition-all duration-500" />
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* ═══ ABOUT / IMAGE SHOWCASE ═══ */}
      {isSectionEnabled("about") && (
      <section className="py-24 bg-white" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Image Collage */}
            <AnimatedSection>
              <div className="relative">
                <div className="grid grid-cols-2 gap-4">
                  <motion.div
                    className="rounded-2xl overflow-hidden shadow-xl"
                    whileHover={{ scale: 1.03 }}
                    transition={{ duration: 0.3 }}
                  >
                    <img
                      src={getImage("classroom")}
                      alt="Students taking an exam in a classroom"
                      className="w-full h-64 object-cover"
                    />
                  </motion.div>
                  <motion.div
                    className="rounded-2xl overflow-hidden shadow-xl mt-8"
                    whileHover={{ scale: 1.03 }}
                    transition={{ duration: 0.3 }}
                  >
                    <img
                      src={getImage("students")}
                      alt="Students studying in class"
                      className="w-full h-64 object-cover"
                    />
                  </motion.div>
                </div>
                {/* Floating accent card */}
                <motion.div
                  className="absolute -bottom-6 -right-4 bg-accent text-white rounded-xl p-5 shadow-xl shadow-accent/30"
                  initial={{ scale: 0.8, opacity: 0 }}
                  whileInView={{ scale: 1, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.4 }}
                >
                  <p className="text-3xl font-bold">95%</p>
                  <p className="text-sm text-white/80">Pass Rate</p>
                </motion.div>
              </div>
            </AnimatedSection>

            {/* Text Content */}
            <AnimatedSection delay={200}>
              <p className="text-accent font-semibold uppercase tracking-wider mb-3 text-sm">{getContent("about_label", "About Us")}</p>
              <h2 className="text-4xl sm:text-5xl font-bold text-navy-900 mb-6 leading-tight">
                {getContent("about_title", "Empowering Ghana's Future Leaders")}
              </h2>
              <p className="text-gray-600 text-base leading-relaxed mb-6">
                {getContent("about_description", "Great Messiah Publication is dedicated to providing top-quality BECE preparation materials that align with the Ghana Education Service curriculum. Our mock exams are crafted by experienced educators who understand what it takes to succeed.")}
              </p>
              <ul className="space-y-4 mb-8">
                {[
                  "Comprehensive marking schemes included",
                  "Updated for the 2024 to date GES syllabus",
                  "Used by 1000+ schools across Ghana",
                  "Instant digital delivery via email",
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-gray-700">
                    <CheckCircle className="w-5 h-5 text-green-500 shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <Button
                asChild
                className="rounded-full bg-navy-900 hover:bg-navy-800 text-white px-8 h-12 group"
              >
                <Link to="/mock-exams">
                  Explore All Products
                  <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>
            </AnimatedSection>
          </div>
        </div>
      </section>
      )}

      {/* ═══ FEATURES SECTION ═══ */}
      {isSectionEnabled("features") && (
      <section className="py-24 bg-gray-50" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection className="text-center mb-16">
            <p className="text-accent font-semibold uppercase tracking-wider mb-3 text-sm">{getContent("features_label", "Why Choose Us")}</p>
            <h2 className="text-4xl sm:text-5xl font-bold text-navy-900 mb-4">
              {getContent("features_title", "Everything You Need to Excel")}
            </h2>
            <p className="text-gray-600 text-base md:text-lg max-w-2xl mx-auto">
              {getContent("features_subtitle", "Join thousands of successful students who trust Great Messiah Publication")}
            </p>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <AnimatedSection key={index} delay={index * 100}>
                <motion.div
                  className="feature-card bg-white p-8 rounded-2xl border border-gray-100 text-center h-full group cursor-default"
                  whileHover={{ y: -8, boxShadow: "0 25px 50px rgba(0,0,0,0.1)" }}
                  transition={{ duration: 0.3 }}
                  data-testid={`feature-card-${index}`}
                >
                  <div className="w-16 h-16 bg-navy-900/5 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:bg-accent/10 group-hover:scale-110 transition-all duration-300">
                    <feature.icon className="w-8 h-8 text-navy-900 group-hover:text-accent transition-colors duration-300" />
                  </div>
                  <h3 className="text-lg font-bold text-navy-900 mb-3">{feature.title}</h3>
                  <p className="text-gray-600 leading-relaxed text-sm">{feature.description}</p>
                </motion.div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* ═══ RECOMMENDED FOR YOU ═══ */}
      {isSectionEnabled("recommendations") && <RecommendationsCarousel />}

      {/* ═══ FEATURED PRODUCTS ═══ */}
      {isSectionEnabled("featured_products") && (
      <section className="py-24 bg-white" data-testid="featured-products-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
              <div>
                <p className="text-accent font-semibold uppercase tracking-wider mb-3 text-sm">{getContent("featured_label", "Popular Products")}</p>
                <h2 className="text-4xl sm:text-5xl font-bold text-navy-900">
                  {getContent("featured_title", "Trending This Month")}
                </h2>
              </div>
              <Button
                variant="outline"
                asChild
                className="rounded-full border-2 border-navy-900 text-navy-900 hover:bg-navy-900 hover:text-white self-start group"
                data-testid="view-all-products-btn"
              >
                <Link to="/mock-exams">
                  View All Products
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>
            </div>
          </AnimatedSection>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-gray-100 rounded-2xl h-96 animate-pulse" />
              ))}
            </div>
          ) : featuredProducts.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {featuredProducts.map((product, index) => (
                <AnimatedSection key={product.id} delay={index * 80}>
                  <ProductCard product={product} />
                </AnimatedSection>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <img src="/logo.png" alt="Great Messiah Publication" className="w-16 h-16 mx-auto mb-4 object-contain opacity-30 grayscale" />
              <p className="text-gray-500">No featured products available yet.</p>
            </div>
          )}
        </div>
      </section>
      )}

      {/* ═══ SUCCESS STORIES / TESTIMONIALS ═══ */}
      {isSectionEnabled("testimonials") && (
      <section className="py-24 bg-gray-50" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Image Side */}
            <AnimatedSection>
              <div className="relative">
                <motion.div
                  className="rounded-2xl overflow-hidden shadow-2xl"
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.3 }}
                >
                  <img
                    src={getImage("happyStudents")}
                    alt="Happy students celebrating their academic success"
                    className="w-full h-96 object-cover"
                  />
                </motion.div>
                {/* Floating quote card */}
                <motion.div
                  className="absolute -bottom-6 -left-4 sm:-left-8 bg-white rounded-xl p-5 shadow-xl max-w-xs border border-gray-100"
                  initial={{ scale: 0.8, opacity: 0 }}
                  whileInView={{ scale: 1, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.5 }}
                >
                  <div className="flex gap-1 mb-2">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-sm text-gray-700 italic">"Best BECE prep materials in Ghana!"</p>
                  <p className="text-xs text-gray-500 mt-1">- Parent Review</p>
                </motion.div>
              </div>
            </AnimatedSection>

            {/* Testimonials Side */}
            <div>
              <AnimatedSection>
                <p className="text-accent font-semibold uppercase tracking-wider mb-3 text-sm">{getContent("testimonials_label", "Success Stories")}</p>
                <h2 className="text-4xl sm:text-5xl font-bold text-navy-900 mb-10">
                  {getContent("testimonials_title", "Trusted by Students & Teachers")}
                </h2>
              </AnimatedSection>

              <div className="space-y-6">
                {testimonials.map((testimonial, index) => (
                  <AnimatedSection key={index} delay={index * 150}>
                    <motion.div
                      className="bg-white p-6 rounded-xl border border-gray-100 group"
                      whileHover={{ x: 8, boxShadow: "0 10px 40px rgba(0,0,0,0.08)" }}
                      transition={{ duration: 0.3 }}
                      data-testid={`testimonial-${index}`}
                    >
                      <p className="text-gray-700 leading-relaxed mb-4">
                        "{testimonial.content}"
                      </p>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-navy-900/10 flex items-center justify-center text-navy-900 font-bold text-sm">
                          {testimonial.name.charAt(0)}
                        </div>
                        <div>
                          <p className="font-semibold text-navy-900 text-sm">{testimonial.name}</p>
                          <p className="text-xs text-gray-500">{testimonial.role}</p>
                        </div>
                      </div>
                    </motion.div>
                  </AnimatedSection>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* ═══ CTA SECTION ═══ */}
      {isSectionEnabled("cta") && (
      <section className="relative py-24 overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('${getImage("examHall")}')` }} />
        <div className="absolute inset-0 bg-navy-900/90" />
        {/* Animated gradient accent */}
        <motion.div
          className="absolute top-0 right-0 w-96 h-96 bg-accent/20 rounded-full blur-3xl"
          animate={{ x: [0, 50, 0], y: [0, -30, 0] }}
          transition={{ duration: 12, repeat: Infinity }}
        />
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <AnimatedSection>
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              {getContent("cta_title", "Ready to Start Preparing?")}
            </h2>
            <p className="text-lg text-gray-300 mb-10 max-w-2xl mx-auto">
              {getContent("cta_subtitle", "Get instant access to quality mock examinations. Individual exams start at just GHS 15.00.")}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                asChild
                className="btn-pill bg-accent hover:bg-accent/90 text-white text-base h-14 shadow-lg shadow-accent/30 group"
                data-testid="cta-browse-btn"
              >
                <Link to="/mock-exams">
                  {getContent("cta_primary", "Browse Mock Exams")}
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                className="btn-pill border-2 border-white/50 text-white hover:bg-white/10 hover:border-white text-base h-14"
              >
                <Link to="/school-services">
                  {getContent("cta_secondary", "Contact for Bulk Orders")}
                </Link>
              </Button>
            </div>
          </AnimatedSection>
        </div>
      </section>
      )}
    </div>
  );
}
